"use client";
import { updateCurrentUserSubscriptionPlan } from "@/hooks/QueryHooks/ReactQueryCachedDataManager";
import { useUpdateUserSubscriptionMutation } from "@/hooks/QueryHooks/ReactQueryWrapperMutationHooks";
import {
  useFetchCurrentUserQuery,
  useStripeCheckout,
  useStripeGetCustomerPortal,
} from "@/hooks/QueryHooks/ReactQueryWrapperhooks";
import { cn } from "@/lib/utils";
import {
  BasePricingBlockProps,
  InternalCreditsPriceID,
  InternalSubscriptionPriceID,
} from "@/typings";
import Constants from "@/utils/Constants";
import { raleway } from "@/utils/FontsConst";
import { getSignupPath } from "@/utils/NavigationUtils";
import { toastError, toastInfo } from "@/utils/ToastsManager";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { FaCircleCheck } from "react-icons/fa6";
import ButtonWithLoading from "../ButtonWithLoading";
import ConfirmModalContainer from "../Feedback/ConfirmModalContainer";

export default function PricingBlock({
  cost,
  credit,
  themeColor,
  badgeText,
  name,
  bulletPoints,
  internalPriceID,
  CTA,
  disabled,
  isCurrentPlan,
  userAlreadyHasPlan,
  customCardClass,
  planChangedCallback,
  receivedClientSecretCallback,
}: BasePricingBlockProps & {
  userAlreadyHasPlan: boolean;
  isCurrentPlan: boolean;
  planChangedCallback?: (newPlan: InternalSubscriptionPriceID) => void;
  receivedClientSecretCallback?: (clientSecret: string) => void;
}) {
  const { data: currentUser } = useFetchCurrentUserQuery();
  const { isFetching: fetchingCustomerPortal, refetch: fetchCustomerPortal } =
    useStripeGetCustomerPortal();
  const { isFetching: fetchingStripeCheckout, refetch: stripeCheckout } =
    useStripeCheckout(internalPriceID as InternalCreditsPriceID, false);

  const queryClient = useQueryClient();
  const updateUserSubscriptionMutation = useUpdateUserSubscriptionMutation(
    (variables) => {},
    (result, variables, context) => {
      if (result.url == null) {
        toastInfo("Subscription Updated");
        planChangedCallback?.(internalPriceID as InternalSubscriptionPriceID);
      } else router.push(result.url);
      let newPlan = Constants.priceIDToLocalSubPlan(
        internalPriceID as InternalSubscriptionPriceID
      );
      updateCurrentUserSubscriptionPlan(newPlan, queryClient);
    },
    (error, variables, context) => {
      toastError();
      console.log("failed to subscribe user error: ");
      console.log(error);
    }
  );

  const router = useRouter();
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  function themedText() {
    return (
      `${themeColor == "info" ? "text-info" : ""}` +
      `${themeColor == "accent" ? "text-accent" : ""}` +
      `${themeColor == "primary" ? "text-primary" : ""}`
    );
  }
  function themedBorder() {
    return (
      `${themeColor == "info" ? "border-info" : ""}` +
      `${themeColor == "accent" ? "border-accent" : ""}` +
      `${themeColor == "primary" ? "border-primary" : ""}`
    );
  }
  function themedButton() {
    return (
      `${themeColor == "info" ? "btn-info" : ""}` +
      `${themeColor == "accent" ? "btn-accent" : ""}` +
      `${themeColor == "primary" ? "btn-primary" : ""}`
    );
  }
  function themedBadge() {
    return (
      `${themeColor == "info" ? "badge-info" : ""}` +
      `${themeColor == "accent" ? "badge-accent" : ""}` +
      `${themeColor == "primary" ? "badge-primary" : ""}`
    );
  }

  function renderHeader() {
    return (
      <div className="flex items-center justify-between">
        <p className={"mb-1 text-lg font-semibold " + ` text-${themeColor} `}>
          {name}
        </p>
        {badgeText?.length > 0 && (
          <span className={"badge " + ` badge-${themeColor} `}>
            {badgeText}
          </span>
        )}
      </div>
    );
  }

  function renderCost() {
    return (
      <div className="my-2 flex">
        <p className="my-2 pb-0 font-mono text-4xl font-extrabold text-base-content">
          {`$${cost}/`}
        </p>
        {/* <p
          className={`  mt-auto pb-2 text-sm  font-extralight text-base-content `}
        >
          month
        </p> */}
        <p
          className={` badge my-auto p-2 text-sm ${themedBadge()}`}
        >{`(${credit} Credits)`}</p>
      </div>
    );
  }

  async function handleButtonClick() {
    if (internalPriceID.startsWith("Subscription_")) {
      handleSubscriptionLogicClick();
    } else {
      handlePurchaseLogicClick();
    }
  }

  async function handlePurchaseLogicClick() {
    if (currentUser == undefined) {
      router.push(getSignupPath());
      return;
    }
    stripeCheckout()
      .then(({ data }) => {
        if (data) {
          const { clientSecret, url } = data;
          if (clientSecret != undefined) {
            receivedClientSecretCallback?.(clientSecret);
          } else {
            router.push(url);
          }
        }
      })
      .catch((error) => {
        toastError();
        console.log("failed with error: ");
        console.log(error);
      });
  }

  function renderUpdatePlanConfirmation() {
    if (isShowingConfirmation == false) return;
    return (
      <ConfirmModalContainer
        title={"Change Plan"}
        confirmButtonText={"Change Now"}
        cancelButtonText={"Cancel"}
        modalContent={
          <div className="my-4">
            <span className="text-xl">
              By confirming this change, your new plan will take effect
              immediately and you will be charged now for the new total amount.
              Are you sure you want to proceed
            </span>
          </div>
        }
        onModalDismiss={(didConfirm) => {
          if (didConfirm == true) {
            updateUserSubscriptionMutation.mutateAsync({
              internalPriceID: internalPriceID as InternalSubscriptionPriceID,
            });
          }
          setIsShowingConfirmation(false);
        }}
      />
    );
  }

  async function handleSubscriptionLogicClick() {
    if (currentUser == undefined) {
      router.push(getSignupPath());
      return;
    }
    if (isCurrentPlan) {
      fetchCustomerPortal()
        .then(({ data: url }) => {
          if (url) router.push(url);
        })
        .catch((error) => {
          toastError();
        });
      return;
    }
    if (userAlreadyHasPlan) setIsShowingConfirmation(true);
    else
      updateUserSubscriptionMutation.mutateAsync({
        internalPriceID: internalPriceID as InternalSubscriptionPriceID,
      });
  }

  function renderBuyNowButton() {
    let isButtonLoading =
      fetchingCustomerPortal ||
      fetchingStripeCheckout ||
      updateUserSubscriptionMutation.isPending;
    return (
      <ButtonWithLoading
        isLoading={isButtonLoading}
        disabled={disabled}
        className={`btn mt-6 w-full ${themedButton()} transition-all`}
        onClick={handleButtonClick}
      >
        {CTA}
      </ButtonWithLoading>
    );
  }

  function renderBulletPoints() {
    if (bulletPoints.length <= 1) return;
    return (
      <div className="flex w-full flex-col">
        <div className="divider w-full"></div>
        <div
          className={`flex flex-grow flex-col space-y-3 px-6 ${raleway.className} font-semibold`}
        >
          {bulletPoints.map((x, index) => (
            <div key={index} className="flex items-start space-x-2">
              <div className={`mt-1 flex ${themedText()}`}>
                <FaCircleCheck />
              </div>
              <p className="text-xl text-base-content">{x}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  function renderCurrentPlan() {
    if (isCurrentPlan == false) return;
    return (
      <div
        className={cn(
          "badge badge-lg w-full rounded-t-none text-lg font-bold",
          themedBadge()
        )}
      >
        Current Plan
      </div>
    );
  }
  return (
    <div
      className={cn(
        "card h-full rounded-3xl bg-base-200 shadow-md",
        isCurrentPlan ? `border-4 ${themedBorder()}` : "",
        customCardClass
      )}
    >
      {renderUpdatePlanConfirmation()}
      <div className="py-6">
        <div className="flex flex-col justify-between px-6 ">
          {renderHeader()}
          {renderCost()}
          {bulletPoints.length == 1 && (
            <p className="text-sm text-base-content">{bulletPoints[0]}</p>
          )}
          {renderBuyNowButton()}
        </div>
        {renderBulletPoints()}
      </div>
      {renderCurrentPlan()}
    </div>
  );
}
